import type { PartyStatus } from '@runkr/schema'

/** A plain copy of the party room's state (`PartyState`). */
export interface PartySnapshot {
  code: string
  leaderSessionId: string
  status: PartyStatus
  members: { sessionId: string; displayName: string; connected: boolean }[]
}

export interface PartyMemberView {
  sessionId: string
  name: string
  connected: boolean
  isLeader: boolean
  isMe: boolean
}

/** D-053 slot `party.state` (T2.7): what the party panel and bar show. */
export interface PartyView {
  code: string
  /** `<origin>/r/<CODE>`, for Copy link. */
  link: string
  status: PartyStatus
  /** In join order (≤ 4). */
  members: PartyMemberView[]
  isLeader: boolean
  /** One status line for the panel and the bar, null for none. */
  notice: string | null
}

export const WAITING_FOR_LEADER = 'Waiting for the party leader…'
export const QUEUE_FAILED = 'Could not find a race. Try again.'
export const FINDING_RACE = 'Finding a race for the party…'

export function partyView(
  snap: PartySnapshot,
  me: string,
  link: string,
  notice: string | null,
): PartyView {
  const isLeader = snap.leaderSessionId !== '' && snap.leaderSessionId === me
  return {
    code: snap.code,
    link,
    status: snap.status,
    members: snap.members.map((m) => ({
      sessionId: m.sessionId,
      name: m.displayName,
      connected: m.connected,
      isLeader: m.sessionId === snap.leaderSessionId,
      isMe: m.sessionId === me,
    })),
    isLeader,
    // A member who became the leader no longer waits for one.
    notice: isLeader && notice === WAITING_FOR_LEADER ? null : notice,
  }
}

/** The bar's line: the notice, else what the party is doing, else who presses Race. */
export function partyLine(view: PartyView): string {
  if (view.notice) return view.notice
  if (view.status === 'queueing') return FINDING_RACE
  return view.isLeader ? 'You lead: Race takes everyone.' : WAITING_FOR_LEADER
}
